import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Users, FileText, DollarSign, CheckCircle, Clock, Package, Activity, X, Loader2 } from 'lucide-react';
import { apiRequest } from '../utils/auth';

export default function Dashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [activePreview, setActivePreview] = useState(null);
  const previewTimeoutRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAnalytics();
    fetchActivityLogs();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const data = await apiRequest('/api/analytics/dashboard');
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      const data = await apiRequest('/api/activity-logs?limit=10');
      setActivityLogs(data.logs || []);
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const previewEndpoints = {
    'Onboard Customers': { endpoint: '/api/dashboard/preview/onboarding', navigateTo: '/onboarding' },
    'Onboarding Customers': { endpoint: '/api/dashboard/preview/onboarding', navigateTo: '/onboarding' },
    'Existing Customers': { endpoint: '/api/dashboard/preview/customers', navigateTo: '/customers' },
    'New Customers (FY)': { endpoint: '/api/dashboard/preview/customers', navigateTo: '/customers' },
    'Total RFQs': { endpoint: '/api/dashboard/preview/rfqs', navigateTo: '/reports' },
    'Potential Value': { endpoint: '/api/dashboard/preview/rfqs', navigateTo: '/reports' },
    'Quotations Sent': { endpoint: '/api/dashboard/preview/quotations-sent', navigateTo: '/reports' },
    'Pending Quotations': { endpoint: '/api/dashboard/preview/pending', navigateTo: '/reports?status=Pending' },
    'PO Accepted': { endpoint: '/api/dashboard/preview/po-accepted', navigateTo: '/reports?status=PO Accepted' },
    'RFQ Rejected': { endpoint: '/api/dashboard/preview/po-rejected', navigateTo: '/reports?status=RFQ Rejected' },
    'Denied RFQs': { endpoint: '/api/dashboard/preview/denied', navigateTo: '/reports?status=Denied' },
    'In Progress': { endpoint: '/api/dashboard/preview/ongoing', navigateTo: '/reports?status=Ongoing' },
    'Order Value (PO Total)': { endpoint: '/api/dashboard/preview/po-accepted', navigateTo: '/reports?status=PO Accepted' },
  };

  const handleTileHover = async (e, title) => {
    const config = previewEndpoints[title];
    if (!config) return;

    // Clear any existing timeout
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }

    const rect = e.currentTarget.getBoundingClientRect();
    setPreviewPosition({ 
      x: rect.left + rect.width / 2, 
      y: rect.bottom + 10 
    });
    setActivePreview(title);
    setPreviewLoading(true);

    try {
      const data = await apiRequest(config.endpoint);
      setPreviewData({ title, records: data.records || [] });
    } catch (error) {
      setPreviewData({ title, records: [], error: true });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleTileLeave = () => {
    previewTimeoutRef.current = setTimeout(() => {
      setActivePreview(null);
      setPreviewData(null);
    }, 200);
  };

  const handlePreviewEnter = () => {
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }
  };

  const handlePreviewLeave = () => {
    setActivePreview(null);
    setPreviewData(null);
  };

  const handleTileClick = (title) => {
    const config = previewEndpoints[title];
    if (config) {
      navigate(config.navigateTo);
    }
  };

  const renderPreviewContent = () => {
    if (!previewData) return null;
    
    const { title, records } = previewData;
    
    if (records.length === 0) {
      return <p className="text-sm text-gray-500 text-center py-4">No data available</p>;
    }

    switch (title) {
      case 'Total Customers':
      case 'Existing Customers':
      case 'New Customers (FY)':
      case 'Onboarding Customers':
        return (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b">
                <th className="text-left py-1 px-2">Company</th>
                <th className="text-left py-1 px-2">City</th>
                <th className="text-left py-1 px-2">Contact</th>
                <th className="text-left py-1 px-2">Date</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-1 px-2 truncate max-w-[120px]">{r.company_name}</td>
                  <td className="py-1 px-2">{r.city || '-'}</td>
                  <td className="py-1 px-2 truncate max-w-[80px]">{r.contact_person || '-'}</td>
                  <td className="py-1 px-2">{r.created_at?.split('T')[0] || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      
      case 'Total RFQs':
      case 'Potential Value':
      case 'Total RFQ Received':
        return (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b">
                <th className="text-left py-1 px-2">Customer</th>
                <th className="text-left py-1 px-2">Part No</th>
                <th className="text-left py-1 px-2">Date</th>
                <th className="text-left py-1 px-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-1 px-2">{r.customer_code}</td>
                  <td className="py-1 px-2 truncate max-w-[80px]">{r.part_no}</td>
                  <td className="py-1 px-2">{r.rfq_date || '-'}</td>
                  <td className="py-1 px-2">
                    <span className={`px-1 py-0.5 rounded text-[10px] ${
                      r.status === 'Completed' ? 'bg-green-100 text-green-700' :
                      r.status === 'Ongoing' ? 'bg-blue-100 text-blue-700' :
                      r.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      case 'PO Accepted':
        return (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b">
                <th className="text-left py-1 px-2">Customer</th>
                <th className="text-left py-1 px-2">Part No</th>
                <th className="text-right py-1 px-2">Qty</th>
                <th className="text-right py-1 px-2">Value</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-1 px-2">{r.customer_code}</td>
                  <td className="py-1 px-2 truncate max-w-[80px]">{r.part_no}</td>
                  <td className="py-1 px-2 text-right">{r.rfq_qty?.toLocaleString() || '-'}</td>
                  <td className="py-1 px-2 text-right">₹{r.estimated_business?.toLocaleString() || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      case 'RFQ Rejected':
      case 'PO Rejected':
      case 'Denied RFQs':
        return (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b">
                <th className="text-left py-1 px-2">Customer</th>
                <th className="text-left py-1 px-2">Part No</th>
                <th className="text-left py-1 px-2">RFQ Date</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-1 px-2">{r.customer_code}</td>
                  <td className="py-1 px-2 truncate max-w-[100px]">{r.part_no}</td>
                  <td className="py-1 px-2">{r.rfq_date || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      case 'Quotations Pending':
      case 'Pending Quotations':
        return (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b">
                <th className="text-left py-1 px-2">Customer</th>
                <th className="text-left py-1 px-2">Part No</th>
                <th className="text-left py-1 px-2">Date</th>
                <th className="text-right py-1 px-2">Qty</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-1 px-2">{r.customer_code}</td>
                  <td className="py-1 px-2 truncate max-w-[80px]">{r.part_no}</td>
                  <td className="py-1 px-2">{r.rfq_date || '-'}</td>
                  <td className="py-1 px-2 text-right">{r.rfq_qty?.toLocaleString() || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      case 'Quotations Submitted':
      case 'Quotations Sent':
      case 'In Progress':
        return (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b">
                <th className="text-left py-1 px-2">Customer</th>
                <th className="text-left py-1 px-2">Part No</th>
                <th className="text-right py-1 px-2">Quoted</th>
                <th className="text-left py-1 px-2">Date</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-1 px-2">{r.customer_code}</td>
                  <td className="py-1 px-2 truncate max-w-[80px]">{r.part_no}</td>
                  <td className="py-1 px-2 text-right">₹{r.quoted_price?.toLocaleString() || '-'}</td>
                  <td className="py-1 px-2">{r.quotation_date || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      default:
        return <p className="text-sm text-gray-500 text-center py-4">No preview available</p>;
    }
  };

  const stats = analytics ? [
    { title: 'Onboarding Customers', value: (analytics.onboarding_customers || analytics.onboard_customers || 0).toString(), icon: Users, color: 'bg-orange-500' },
    { title: 'Existing Customers', value: (analytics.existing_customers || 0).toString(), icon: Users, color: 'bg-blue-500' },
    { title: 'New Customers (FY)', value: (analytics.new_customers_fy || analytics.new_customers || 0).toString(), icon: Users, color: 'bg-green-500' },
    { title: 'Total RFQs', value: (analytics.total_rfqs || 0).toString(), icon: FileText, color: 'bg-purple-500' },
    { title: 'Potential Value', value: `₹${(analytics.potential_value || 0).toLocaleString('en-IN')}`, icon: DollarSign, color: 'bg-purple-400' },
    { title: 'Quotations Sent', value: (analytics.quotations_sent || 0).toString(), icon: CheckCircle, color: 'bg-teal-500' },
    { title: 'Pending Quotations', value: (analytics.pending_quotations || 0).toString(), icon: Clock, color: 'bg-yellow-500' },
    { title: 'PO Accepted', value: (analytics.po_accepted_count || 0).toString(), icon: CheckCircle, color: 'bg-emerald-500' },
    { title: 'RFQ Rejected', value: (analytics.rfq_rejected_count || 0).toString(), icon: Clock, color: 'bg-red-500' },
    { title: 'Denied RFQs', value: (analytics.denied_count || 0).toString(), icon: FileText, color: 'bg-gray-500' },
    { title: 'In Progress', value: (analytics.in_progress_count || 0).toString(), icon: Activity, color: 'bg-indigo-500' },
    { title: 'Order Value (PO Total)', value: `₹${(analytics.order_value || 0).toLocaleString('en-IN')}`, icon: DollarSign, color: 'bg-green-600' },
  ] : [];

  return (
    <Layout>
      <div data-testid="dashboard-page">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-[#0A2540]">Dashboard Overview</h2>
          <p className="text-sm text-gray-600 mt-1">Real-time business metrics and performance indicators</p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-4" />
            <p className="text-gray-500">Loading analytics...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                const hasPreview = previewEndpoints[stat.title];
                return (
                  <Card
                    key={index}
                    data-testid={`stat-card-${index}`}
                    className={`p-5 hover:shadow-lg transition-shadow border-gray-200 ${hasPreview ? 'cursor-pointer' : ''}`}
                    onMouseEnter={(e) => hasPreview && handleTileHover(e, stat.title)}
                    onMouseLeave={handleTileLeave}
                    onClick={() => hasPreview && handleTileClick(stat.title)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm text-gray-600 mb-2">{stat.title}</p>
                        <p className="text-2xl font-bold text-[#0A2540]">{stat.value}</p>
                      </div>
                      <div className={`${stat.color} p-3 rounded-lg`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Preview Tooltip */}
            {activePreview && (
              <div 
                className="fixed z-50 bg-white shadow-xl rounded-lg border border-gray-200 p-3 min-w-[350px] max-w-[450px]"
                style={{ 
                  left: Math.min(previewPosition.x - 175, window.innerWidth - 470),
                  top: previewPosition.y,
                  maxLeft: 20
                }}
                onMouseEnter={handlePreviewEnter}
                onMouseLeave={handlePreviewLeave}
              >
                <div className="flex items-center justify-between mb-2 pb-2 border-b">
                  <h4 className="text-sm font-semibold text-[#0A2540]">{activePreview} - Latest 5</h4>
                  <button onClick={handlePreviewLeave} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {previewLoading ? (
                  <p className="text-sm text-gray-500 text-center py-4">Loading...</p>
                ) : (
                  renderPreviewContent()
                )}
                <p className="text-[10px] text-gray-400 mt-2 text-center">Click tile to view all</p>
              </div>
            )}

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="p-6 border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <Activity className="w-5 h-5 text-[#0A2540]" />
                  <h3 className="text-lg font-semibold text-[#0A2540]">Recent Activities</h3>
                </div>
                <div className="space-y-3">
                  {activityLogs.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">No recent activity</p>
                  ) : (
                    activityLogs.slice(0, 5).map((log, i) => (
                      <div key={log._id || i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{log.description}</p>
                          <p className="text-xs text-gray-500">{log.user || 'System'}</p>
                        </div>
                        <span className="text-xs text-gray-400">{formatTimeAgo(log.created_at)}</span>
                      </div>
                    ))
                  )}
                </div>
              </Card>

              <Card className="p-6 border-gray-200">
                <h3 className="text-lg font-semibold text-[#0A2540] mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <button
                    data-testid="quick-action-new-rfq"
                    onClick={() => navigate('/rfq')}
                    className="w-full p-3 text-left bg-[#0A2540] text-white rounded-lg hover:bg-[#0d2f52] transition-colors"
                  >
                    <p className="font-medium">Create New RFQ</p>
                    <p className="text-xs text-white/70">Start a new request for quotation</p>
                  </button>
                  <button
                    data-testid="quick-action-register-customer"
                    onClick={() => navigate('/customer-registration')}
                    className="w-full p-3 text-left bg-white border-2 border-[#0A2540] text-[#0A2540] rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <p className="font-medium">Register Customer</p>
                    <p className="text-xs text-gray-600">Add a new customer to the system</p>
                  </button>
                  <button
                    data-testid="quick-action-view-reports"
                    onClick={() => navigate('/reports')}
                    className="w-full p-3 text-left bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <p className="font-medium">View Reports</p>
                    <p className="text-xs text-gray-600">Access business reports and analytics</p>
                  </button>
                </div>
              </Card>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
